/**
 * @format
 */
import { ExportPuppeterService } from '@/scripts/services/puppeteer/export-puppeter-service';
import TemplateActions from '@/mixins/template-actions';
import { InvoicesService } from '@/scripts/services/invoices/invoices-service.js';
import bankIcon from '@/assets/images/SVG/check-icon.svg';

export const MdfInvoiceMixin = {
    mixins: [TemplateActions],

    computed: {
        contextModeMixin() {
            if (this.$store.state.context.client.id) {
                return 'client';
            } else if (this.$store.state.context.agency.id) {
                return 'agency';
            }
        },
    },

    methods: {
        downloadMdfInvoicePdf(invoiceDetails) {
            return this.generateMDFTemplate(invoiceDetails, this.download);
        },

        printMdfInvoicePdf(invoiceDetails) {
            return this.generateMDFTemplate(invoiceDetails, this.printOnSite);
        },

        generateReportJSON({ pdfImages = [], invoiceDetails }) {
            const template =
                this.contextModeMixin === 'agency' ? 'mdf-invoice-agency' : 'mdf-invoice-client';
            let paymentMethod;
            if (this.contextModeMixin === 'agency' && invoiceDetails.isMarkedAsPaid) {
                paymentMethod = { paymentMethod: 'Marked as Paid' };
            } else if (
                this.contextModeMixin === 'agency' &&
                invoiceDetails.paymentMethod &&
                invoiceDetails.paymentMethod.paymentType
            ) {
                paymentMethod = {
                    paymentMethod: invoiceDetails.paymentMethod.paymentType,
                    cardDetails: invoiceDetails.paymentMethod.cardDetails,
                };
            } else if (this.contextModeMixin === 'client') {
                paymentMethod = { paymentMethod: invoiceDetails.paymentMethod };
            }
            return {
                template: template,
                format: 'pdf',
                deliveryMethod: 'attachment',

                data: {
                    pdfImages,
                    totalPages: pdfImages ? pdfImages.length + 1 : 1,
                    addressBilled: invoiceDetails.address,
                    isPaid: invoiceDetails.isPaid,
                    isNotPaid: !invoiceDetails.isPaid,
                    invoiceIssued: invoiceDetails.invoiceIssued,
                    paymentDate: invoiceDetails.isPaid
                        ? invoiceDetails.paymentDate
                        : invoiceDetails.paymentDue,
                    orderNumber: invoiceDetails.confirmationNumber,
                    ...paymentMethod,
                    invoiceNumber: invoiceDetails.invoiceNumber,
                    item: invoiceDetails.items.map(({ FundTypeName }) => FundTypeName).join(', '),
                    amount: invoiceDetails.isMarkedAsPaid
                        ? invoiceDetails.baseFee
                        : invoiceDetails.amount,
                    totalClientAmount: invoiceDetails.amount,
                    invoiceId: invoiceDetails.invoiceNumber,
                    isMarkedAsPaid: invoiceDetails.isMarkedAsPaid,

                    // partner
                    payTo: invoiceDetails.companyName,

                    // agency
                    baseFee: invoiceDetails.baseFee,
                    glasshiveFee: invoiceDetails.glasshiveFee,
                    processingFee: invoiceDetails.processingFee,
                    payableTo: invoiceDetails.companyName,
                    fundTitle: invoiceDetails.fundTitle,
                },
            };
        },

        async getFileFromBlobUrl(fileURL) {
            let dataImg = await fetch(fileURL);
            const buff = await dataImg.blob();
            const file = new File([buff], 'file', {
                type: buff.type,
                size: buff.size,
            });
            return file;
        },

        async generateMDFTemplate(invoiceDetails, callback) {
            const fileURL =
                invoiceDetails.proofOfPayment.length &&
                invoiceDetails.proofOfPayment[0].downloadUrl;

            let pdfImages = null;

            if (fileURL) {
                const formData = new FormData();
                const file = await this.getFileFromBlobUrl(fileURL);
                formData.append('pdf', file, 'file');
                await ExportPuppeterService.pdfToImage(formData).then(({ data: { data } }) => {
                    pdfImages = data.map((image, index) => ({
                        url: image,
                        page: index + 2,
                    }));
                });
            }

            const invoiceTemplateDate = this.generateReportJSON({
                pdfImages,
                invoiceDetails: invoiceDetails,
            });

            return new Promise((resolve, reject) => {
                ExportPuppeterService.generate(invoiceTemplateDate)
                    .then((data) => {
                        callback(data.data, invoiceDetails.invoiceNumber);
                        resolve();
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'An error occurred while fetching your invoice.',
                            { error },
                        );
                        reject(error);
                    });
            });
        },

        getBilledToDetailsMixin(paymentId, companyName, agencyId) {
            return new Promise((resolve, reject) => {
                const paymentMode = this.invoiceMode === 'partner' ? 'client' : 'agency';
                InvoicesService.getPaymentMethod(agencyId, paymentMode)
                    .then(({ data: { data } }) => {
                        const [payment] = data.filter((item) => item.Id === paymentId);
                        const fullAddress = payment.FullAddress || '';
                        const state = payment.State || '';
                        const zipCode = payment.ZipCode || '';

                        const isCard = payment.Type === 'card';
                        const paymentType = isCard ? 'Credit Card' : 'Bank Account';
                        const endingNumbers = isCard
                            ? `${payment.CardType} Ending on ${payment.CardLastFour}`
                            : `Checking Account ending in ${payment.BankAccountLastFour}`;
                        const imageCard = isCard
                            ? this.$helpers.setCardTypeData(payment.CardType).cardImageSource
                            : bankIcon;

                        resolve({
                            billedToDetails: this.generateAddressMixin(
                                companyName,
                                fullAddress,
                                state,
                                zipCode,
                            ),
                            paymentType: `${payment.NickName} ${paymentType}`,
                            cardDetails: `${endingNumbers}`,
                            imageCard: imageCard,
                        });
                    })
                    .catch((error) => {
                        reject(error);
                    });
            });
        },

        generateAddressMixin(companyName, fullAddress, state, zipCode) {
            const billedTo = [];
            if (companyName) {
                billedTo.push(companyName);
            }
            if (fullAddress) {
                billedTo.push(fullAddress);
            }
            if (state) {
                billedTo.push(state);
            }
            if (zipCode) {
                billedTo.push(zipCode);
            }
            return billedTo.join(', ');
        },
    },
};
